/**
 * StepBasics — collect identity + contact basics.
 *
 * Writes directly to useCvState via updatePersonalInfo.
 * Common fields (firstName/lastName/email/phone/city) live in commonInfo;
 * title is version-specific and lives in data.personalInfo.
 */

import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import type { CVData } from '../../../types';
import type { CommonInfo } from '../../../hooks/useCvState';

interface StepBasicsProps {
  commonInfo: CommonInfo;
  data: CVData;
  updatePersonalInfo: (field: keyof CVData['personalInfo'], value: string) => void;
}

export function StepBasics({ commonInfo, data, updatePersonalInfo }: StepBasicsProps) {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 className="text-2xl font-bold">Parlons de toi</h1>
        <p className="text-sm text-white/60">
          Les informations de base qui apparaîtront en haut du CV.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="wiz-firstName" className="text-sm text-white/80">Prénom</Label>
          <Input
            id="wiz-firstName"
            type="text"
            value={commonInfo.firstName || ''}
            onChange={(e) => updatePersonalInfo('firstName', e.target.value)}
            placeholder="Marie"
            className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="wiz-lastName" className="text-sm text-white/80">Nom</Label>
          <Input
            id="wiz-lastName"
            type="text"
            value={commonInfo.lastName || ''}
            onChange={(e) => updatePersonalInfo('lastName', e.target.value)}
            placeholder="Durand"
            className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="wiz-title" className="text-sm text-white/80">
          Titre / poste visé
        </Label>
        <Input
          id="wiz-title"
          type="text"
          value={data.personalInfo.title || ''}
          onChange={(e) => updatePersonalInfo('title', e.target.value)}
          placeholder="Développeuse Full-Stack · React / Node"
          className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
        />
        <p className="text-xs text-white/40">
          Affiché juste sous ton nom. Garde-le court et précis.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="wiz-email" className="text-sm text-white/80">Email</Label>
          <Input
            id="wiz-email"
            type="email"
            value={commonInfo.email || ''}
            onChange={(e) => updatePersonalInfo('email', e.target.value)}
            placeholder="marie.durand@email.com"
            className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="wiz-phone" className="text-sm text-white/80">Téléphone</Label>
          <Input
            id="wiz-phone"
            type="tel"
            value={commonInfo.phone || ''}
            onChange={(e) => updatePersonalInfo('phone', e.target.value)}
            placeholder="06 12 34 56 78"
            className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="wiz-city" className="text-sm text-white/80">Ville</Label>
        <Input
          id="wiz-city"
          type="text"
          value={commonInfo.city || ''}
          onChange={(e) => updatePersonalInfo('city', e.target.value)}
          placeholder="Paris"
          className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
        />
      </div>
    </div>
  );
}
